import { ListingInfo } from '@apeiron/common'
import {
  CurrencySettleOrderTokenType,
  CurrencyTokenType,
  useCurrencyContext
} from '@apeiron/currency'
import { ApeironCloseButton, useAccountContext, useWeb3 } from '@apeiron/temp'
import { Box, Dialog, Divider, Paper, styled, Typography } from '@mui/material'
import EthereumSVG from '@public/icons/icon-ethereum.svg'
import { CompletePurchaseButton } from '@src/components/planet/detail/CompletePurchaseButton'
import PriceText from '@src/components/share/generic/PriceText'
import SettleOrderTokenSwitcher from '@src/components/share/trade/buyDialogView/SettleOrderTokenSwitcher'
import TradeBuyStepView from '@src/components/share/trade/buyDialogView/TradeBuyStepView'
import TradeQuanatitySection from '@src/components/share/trade/TradeQuantitySection'
import { SKIP_QUANTITY } from '@src/constants/trade'
import usePriceCalculation from '@src/hooks/common/tools/usePriceCalculation'
import { TradeItem } from '@src/types/trade'
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const DialogPaper = styled(Paper)`
  background-color: #262829;
  border-radius: 5px;
  color: #ffffff;
  max-width: 600px;
  width: 100%;
`

const DialogTitle = styled(Box)`
  margin: 24px 0 24px 42px;
  font-size: 26px;
`

const DialogContent = styled(Paper)`
  background-color: #171719;
  display: flex;
  flex-direction: column;
  ${props => props.theme.breakpoints.up('res1024')} {
    padding: 30px 42px 12px 42px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    align-items: center;
    padding: 20px 15px 0px 15px;
  }
`

const ContentItem = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  margin-bottom: 20px;
  padding: 0px 6px;
  ${props => props.theme.breakpoints.up('res1024')} {
    column-gap: 40px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    align-items: center;
    flex-direction: column;
    max-width: 320px;
    row-gap: 10px;
  }
`

const ContentItemDetail = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 20px;
  width: 100%;
  ${props => props.theme.breakpoints.up('res1024')} {
    padding-top: 10px;
  }
`

const ContentItemRow = styled(Box)`
  display: flex;
  color: #ffffff;
`

const AlignedLeftText = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const AlignedRightText = styled(Box)`
  flex: 1;
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 5px;
`

const ItemNameText = styled(Typography)`
  margin-bottom: 10px;
  font-weight: 400;
`

const StyledTradeQuanatitySection = styled(TradeQuanatitySection)`
  .Custom-Label {
    font-size: 16px;
  }
`

const EthPriceText = styled(PriceText)`
  .Custom-Icon {
    width: 18px;
    height: 18px;
  }
  .Custom-Text {
    font-size: 16px;
    font-weight: 300;
  }
`

const NotEnoughFee = styled(Typography)`
  color: #ff0000;
  font-size: 16px;
  font-weight: 300;
  text-align: right;
`

const BottomContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 30px 0;
`

const StyledCloseButton = styled(ApeironCloseButton)`
  position: absolute;
  ${props => props.theme.breakpoints.up('res480')} {
    top: 20px;
    right: 20px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    top: 12px;
    right: 12px;
  }
`

const WaitingContainer = styled(Box)`
  width: 100%;
`

const StyledDivider = styled(Divider)`
  border-color: #80808080;
  width: 100%;
  margin-top: 30px;
  margin-bottom: 30px;
`

const BalanceContainer = styled(Box)`
  display: flex;
  flex-direction: column;
`

const BalanceUpperRow = styled(Box)`
  display: flex;
  flex-direction: row;
  background-color: rgba(163, 50, 255, 0.7);
  padding: 8px 12px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
`

const BalanceLowerRow = styled(Box)`
  background-color: rgba(57, 46, 67, 0.8);
  padding: 8px 12px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;

  ${props => props.theme.breakpoints.down('res1024')} {
    gap: 1px;
  }
`

const BalanceText = styled(Typography)`
  font-weight: 700;
`

const TradeBuyDialog: FC<Props> = (props: Props) => {
  const {
    className,
    isBought,
    isOpened,
    isWaitingApprove,
    isWaitingConfirm,
    item,
    itemName,
    listingInfo,
    totalQuantity = -1,
    onClose,
    onConfirm,
    renderImage
  } = props

  const { t } = useTranslation()

  const [settleOrderTokenType, setSettleOrderTokenType] =
    useState<CurrencySettleOrderTokenType>(CurrencyTokenType.Weth)

  const { fetchBalances, account } = useAccountContext()

  const { ethPrice } = usePriceCalculation(listingInfo)

  const {
    convertCurrencyPrice,
    getUSDPrice,
    getCurrency,
    getApeironDetailPrice,
    getApeironPrice
  } = useCurrencyContext()

  const [quantity, setQuantity] = useState<number>(1)

  const { toWei } = useWeb3()

  const isEnoughQuantity =
    totalQuantity === SKIP_QUANTITY ||
    (quantity > 0 && quantity <= totalQuantity)

  const handleOnQuantityChange = useCallback((value: number) => {
    setQuantity(value)
  }, [])

  const handleOnConfirmClick = useCallback(async () => {
    if (onConfirm) {
      await onConfirm(item, ethPrice, settleOrderTokenType, quantity)
      fetchBalances()
    }
  }, [onConfirm, item, ethPrice, settleOrderTokenType, quantity, fetchBalances])

  const onConditionalClose =
    isWaitingApprove || isWaitingConfirm ? () => {} : onClose

  const currency = getCurrency(settleOrderTokenType)

  const neededPrice = useMemo(() => {
    if (!currency) {
      return BigInt('0')
    }

    const convertedPrice =
      convertCurrencyPrice(
        ethPrice,
        CurrencyTokenType.Weth,
        settleOrderTokenType
      ) * quantity

    return toWei(convertedPrice, currency?.decimals || 0)
  }, [
    convertCurrencyPrice,
    ethPrice,
    quantity,
    settleOrderTokenType,
    toWei,
    currency
  ])

  const hasEnoughBalance =
    BigInt(account.balances?.[settleOrderTokenType]?.amount || '0') >=
    BigInt(neededPrice)

  const handleTokenChange = useCallback(
    async (tokenType: CurrencySettleOrderTokenType) => {
      setSettleOrderTokenType(tokenType)
    },
    []
  )

  useEffect(() => {
    if (!isOpened) {
      setSettleOrderTokenType(CurrencyTokenType.Weth)
    }
  }, [isOpened])

  return (
    <Dialog
      className={className}
      open={isOpened}
      onClose={onConditionalClose}
      PaperComponent={DialogPaper}
    >
      <StyledCloseButton onClick={onConditionalClose} />
      <DialogTitle>{t('dialog.buy.title')}</DialogTitle>
      <DialogContent>
        <ContentItem>
          <ItemNameText>{itemName}</ItemNameText>
          {renderImage()}
          <ContentItemDetail>
            {totalQuantity !== -1 && (
              <StyledTradeQuanatitySection
                quantity={quantity}
                totalQuantity={totalQuantity}
                onChange={handleOnQuantityChange}
              />
            )}
            <ContentItemRow>
              <AlignedLeftText>{t('common.price')}:</AlignedLeftText>
              <AlignedRightText>
                <EthPriceText
                  icon={EthereumSVG}
                  prefix=''
                  displayPrice={getApeironDetailPrice(ethPrice * quantity)}
                />
              </AlignedRightText>
            </ContentItemRow>
            <BalanceContainer>
              <BalanceUpperRow>
                <AlignedLeftText>
                  <BalanceText>
                    {t('expedition.key_and_token.label.balance')}
                  </BalanceText>
                </AlignedLeftText>
                <AlignedRightText>
                  {t('dialog.buy.balance.price_in_usd', {
                    price: getUSDPrice(
                      settleOrderTokenType,
                      account.balances?.[settleOrderTokenType]?.amount,
                      true
                    )
                  })}
                </AlignedRightText>
              </BalanceUpperRow>
              <BalanceLowerRow>
                <EthPriceText
                  icon={account.balances?.[settleOrderTokenType]?.image}
                  prefix=''
                  displayPrice={getApeironPrice(
                    settleOrderTokenType,
                    account.balances?.[settleOrderTokenType]?.amount,
                    {
                      allowedLargeNumberAbbr: false,
                      maxLength: 10,
                      maxDecimal: 5
                    }
                  )}
                />
              </BalanceLowerRow>
            </BalanceContainer>
          </ContentItemDetail>
        </ContentItem>
        <SettleOrderTokenSwitcher
          settleOrderTokenType={settleOrderTokenType}
          sellOrderTokenType={CurrencyTokenType.Weth}
          price={ethPrice * quantity}
          onTokenTypeChange={handleTokenChange}
        />
        {(isWaitingApprove || isWaitingConfirm || isBought) && (
          <WaitingContainer>
            <StyledDivider />
            <TradeBuyStepView
              stepNumber={1}
              isDone={isWaitingConfirm || isBought}
              stepTitle='dialog.buy.step1.title'
              stepHint='dialog.buy.step1.hint'
            />
            {(isWaitingConfirm || isBought) && (
              <TradeBuyStepView
                stepNumber={2}
                isDone={isBought}
                stepTitle='dialog.buy.step2.title'
                stepHint='dialog.buy.step2.hint'
              />
            )}
          </WaitingContainer>
        )}
        <BottomContainer>
          <CompletePurchaseButton
            onClick={isBought ? onClose : handleOnConfirmClick}
            disabled={
              isWaitingApprove ||
              isWaitingConfirm ||
              !isEnoughQuantity ||
              !hasEnoughBalance
            }
          >
            {(isWaitingApprove || isWaitingConfirm || isBought) &&
              t(
                isBought
                  ? 'dialog.buy.purchase_success'
                  : isWaitingConfirm
                  ? 'dialog.buy.step2.waiting_button'
                  : 'dialog.buy.step2.waiting_button'
              )}
          </CompletePurchaseButton>
          {!hasEnoughBalance && (
            <NotEnoughFee>{t('common.no_balance')}</NotEnoughFee>
          )}
        </BottomContainer>
      </DialogContent>
    </Dialog>
  )
}

type Props = {
  className?: string
  isBought: boolean
  isOpened: boolean
  isWaitingApprove: boolean
  isWaitingConfirm: boolean
  item: TradeItem
  itemName: string
  listingInfo: ListingInfo
  totalQuantity?: number
  renderImage: () => ReactNode
  onClose?: () => void
  onConfirm?: (
    item: TradeItem,
    price: number,
    settleOrderTokenType: CurrencySettleOrderTokenType,
    quantity: number
  ) => Promise<boolean>
}

export default TradeBuyDialog
